import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Link } from "wouter";
import { api, type ProjectDetail } from "@/api/client";
import { Button } from "@/components/ui/button";
import { BaziForm } from "@/components/lifekline/BaziForm";
import { LifeKLineChart } from "@/components/lifekline/LifeKLineChart";
import { AnalysisResult } from "@/components/lifekline/AnalysisResult";
import type { BaziInput, LifeDestinyResult } from "@/lifekline/types";
import { 
  ArrowLeft, 
  Loader2, 
  Sparkles, 
  AlertCircle,
  Download,
  RefreshCw
} from "lucide-react";

type PageState = "input" | "loading" | "result" | "error";

export function LifeKLinePage() {
  const [, setLocation] = useLocation();
  const [pageState, setPageState] = useState<PageState>("input");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [result, setResult] = useState<LifeDestinyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [baziInput, setBaziInput] = useState<BaziInput | null>(null);
  
  // Polling for project status
  useEffect(() => {
    if (pageState !== "loading" || !projectId) return;
    
    const pollInterval = setInterval(async () => {
      const response = await api.projects.get(projectId);
      
      if (response.success && response.data) {
        const project = response.data;
        
        if (project.status === "completed" && project.analysis) {
          setResult(project.analysis as unknown as LifeDestinyResult);
          setPageState("result");
          clearInterval(pollInterval);
        } else if (project.status === "failed") {
          setError(project.error || "生成失败，请重试");
          setPageState("error");
          clearInterval(pollInterval);
        }
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [pageState, projectId]);
  
  const handleSubmit = async (input: BaziInput) => {
    setBaziInput(input);
    setError(null);
    setPageState("loading");
    
    try {
      const response = await api.projects.create({
        name: input.name ? `${input.name}的人生K线` : "人生K线分析",
        templateId: "lifekline",
        inputText: JSON.stringify(input), // Store input as JSON string
        baziInput: input,
      });
      
      if (response.success && response.data) {
        setProjectId(response.data.id);
      } else {
        setError(response.error || "创建项目失败");
        setPageState("error");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "未知错误");
      setPageState("error");
    }
  };
  
  const handleRetry = () => {
    setPageState("input");
    setError(null);
    setProjectId(null);
    setResult(null);
  };
  
  const handleExportReport = () => {
    // Use browser print to save as PDF
    window.print();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border no-print">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-500" />
                人生K线
              </h1>
              <p className="text-sm text-muted-foreground">
                基于八字命理的人生运势可视化
              </p>
            </div>
          </div>
          
          {pageState === "result" && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleRetry}>
                <RefreshCw className="w-4 h-4 mr-1" />
                重新分析
              </Button>
              <Button variant="default" size="sm" onClick={handleExportReport}>
                <Download className="w-4 h-4 mr-1" />
                导出报告
              </Button>
            </div>
          )}
        </div>
      </header>
      
      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Input State */}
        {pageState === "input" && (
          <div className="space-y-8">
            {/* Introduction */}
            <section className="text-center py-8">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent mb-4">
                🔮 洞悉命运起伏 预见人生轨迹
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                结合传统八字命理与金融可视化技术，将您的一生运势绘制成类似股票行情的K线图。
                输入您的八字信息，即可生成完整的人生运势分析报告。
              </p>
            </section>
            
            {/* Form */}
            <section className="bg-card rounded-2xl border border-border p-6 md:p-8">
              <BaziForm onSubmit={handleSubmit} isSubmitting={false} />
            </section>
            
            {/* Disclaimer */}
            <section className="text-center text-sm text-muted-foreground">
              <p>
                ⚠️ 免责声明：本工具仅供娱乐与文化研究，命运掌握在自己手中。
                切勿迷信，请理性看待分析结果。
              </p>
            </section>
          </div>
        )}
        
        {/* Loading State */}
        {pageState === "loading" && (
          <div className="flex flex-col items-center justify-center py-24 space-y-6">
            <div className="relative">
              <div className="w-24 h-24 border-4 border-violet-200 dark:border-violet-900 rounded-full animate-pulse" />
              <Loader2 className="w-12 h-12 text-violet-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-spin" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-foreground">正在推演命盘...</h3>
              <p className="text-muted-foreground">
                AI 正在根据您的八字信息生成人生K线图和分析报告，请稍候
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="w-2 h-2 bg-violet-500 rounded-full animate-pulse" />
              这可能需要 30-60 秒...
            </div>
          </div>
        )}
        
        {/* Error State */}
        {pageState === "error" && (
          <div className="flex flex-col items-center justify-center py-24 space-y-6">
            <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-foreground">分析失败</h3>
              <p className="text-muted-foreground">{error}</p>
            </div>
            <Button onClick={handleRetry} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              重新尝试
            </Button>
          </div>
        )}
        
        {/* Result State */}
        {pageState === "result" && result && (
          <div className="space-y-8">
            {/* K-Line Chart */}
            <section className="bg-card rounded-2xl border border-border p-6">
              <LifeKLineChart 
                data={result.chartData} 
                userName={baziInput?.name}
              />
            </section>
            
            {/* Analysis Report */}
            <AnalysisResult 
              analysis={result.analysis}
              chartData={result.chartData}
              userName={baziInput?.name}
            />
            
            {/* Footer */}
            <section className="text-center py-8 text-sm text-muted-foreground no-print">
              <p>© {new Date().getFullYear()} 人生K线 | 仅供娱乐与文化研究，请勿迷信</p>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
